import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractReview, usePostAgreementImportsBySessionIdRowsMutation } from '@client/api';
import { Button, Card, CardContent, CardHeader, CardTitle, HelperText, Typography } from '@client/toolkit';

import { parseSourceFile } from '../utils/parse-source-file';
import { extractErrorCodes } from '../utils/extract-error-codes';

interface StepUploadProps {
  sessionId: string;
  onRowsImported: (contracts: ContractReview[]) => void;
  onBack: () => void;
}

const ACCEPTED_FILE_TYPES =
  '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';

/**
 * Step 2 — client-side parses the uploaded CSV/XLSX into <c>AgreementImportSourceRow[]</c>
 * (array-splitting already happened at the source per decision 1) and posts them to
 * <c>POST /agreement-imports/{id}/rows</c>, which chains parsing (Phase 2) and mapping (Phase 2b).
 */
export function StepUpload({ sessionId, onRowsImported, onBack }: StepUploadProps) {
  const { t: tAgreements } = useTranslation('agreements');
  const { t: tCommon } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [skippedRowCount, setSkippedRowCount] = useState(0);

  const [importRows, { isLoading, error }] = usePostAgreementImportsBySessionIdRowsMutation();

  const errorCodes = extractErrorCodes(error);

  const handleClickSelectFile = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setParseError(null);
    setSkippedRowCount(0);
  };

  const handleContinue = async () => {
    if (!file) return;
    setParseError(null);

    let parsed;
    try {
      parsed = await parseSourceFile(file);
    } catch {
      setParseError(tAgreements('agreementImport.upload.parseError'));
      return;
    }

    if (parsed.rows.length === 0) {
      setParseError(tAgreements('agreementImport.upload.noRowsFound'));
      return;
    }

    setSkippedRowCount(parsed.skippedRowCount);

    try {
      const contracts = await importRows({ sessionId, body: parsed.rows }).unwrap();
      onRowsImported(contracts);
    } catch {
      /* error is surfaced below via the errorCodes list */
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{tAgreements('agreementImport.upload.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <Typography variant="secondary">{tAgreements('agreementImport.upload.description')}</Typography>

        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className="hidden"
            onChange={handleFileChange}
          />
          <Button color="primary-invert" onClick={handleClickSelectFile} disabled={isLoading}>
            {tAgreements('agreementImport.upload.selectFile')}
          </Button>
          {file && (
            <Typography className="text-sm text-white/70">
              {tAgreements('agreementImport.upload.chosenFile', { name: file.name })}
            </Typography>
          )}
        </div>

        {parseError && <HelperText error>{parseError}</HelperText>}
        {skippedRowCount > 0 && !parseError && (
          <HelperText>{tAgreements('agreementImport.upload.skippedRows', { count: skippedRowCount })}</HelperText>
        )}

        {errorCodes.length > 0 && (
          <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4">
            <ul className="list-disc pl-5 text-sm text-red-200 space-y-1">
              {errorCodes.map((code) => (
                <li key={code}>{tAgreements(`agreementImport.validationErrors.${code}`, { defaultValue: code })}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-between">
          <Button color="primary-dark" onClick={onBack}>
            {tCommon('back')}
          </Button>
          <Button color="primary" disabled={!file} progressing={isLoading} onClick={handleContinue}>
            {tAgreements('agreementImport.upload.continueButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
